import React, { ReactElement, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  Alert,
  Box,
  BreadcrumbGroup,
  Container,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import {
  CMS_APP_CONFIG,
  GET_LIST_CONTAINER_DEFINITION,
  GET_LOCATIONS_FOR_SITE,
  LIST_CONTAINER_TYPES,
  MANAGE_CONTAINER_PERMISSIONS,
} from '../../../services/container-management/queries';
import sizeData from '../mocks/size.json';
import {
  LocationInfo,
  LocationDropDown,
  CreateDetails,
  ContainerDefinition,
  CreateContainersRequest,
  CreateContainersResponse,
} from '../../../services/container-management/types';
import SiteInput from '../../../components/SiteInput';
import CreateBin from './ContainerTypes/CreateBin';
import { parseListContainerDefinitionsResponse } from '../../../utils/ContainerManagement/containerDefinitionsHelper';
import Create from './CreateContainer';
import CreateIVTAVT from './ContainerTypes/CreateIVTAVT';
import './style.css';
import { Category } from '../types';
import CreateContainerComponent from './ContainerTypes/CreateContainerComponent';
import CreateContainerAlerts from './CreateContainerAlerts';

export function transformDataForDropdown(locationResponse: [LocationInfo]): any {
  const locationData: { label: string; value: string; locationId: string }[] = [];
  Object.values(locationResponse).forEach((loc) => {
    locationData.push({
      label: loc.locationName,
      value: loc.locationName,
      locationId: loc.locationId,
    });
  });
  return locationData;
}

const CreateContainer = (): ReactElement => {
  const { t } = useTranslation();
  const [containerTypeOptions, setContainerTypeOptions] = React.useState<SelectProps.Option[]>([]);
  const [listContainerTypesError, setListContainerTypesError] = React.useState<string>('');
  const [rooms, setRooms] = React.useState<LocationDropDown[]>([]);
  const [createContainersRequest, setCreateContainersRequest] =
    React.useState<CreateContainersRequest | null>(null);
  const [createContainersResponse, setCreateContainersResponse] =
    React.useState<CreateContainersResponse | null>(null);
  const [formValuesBin, setFormValuesBin] = React.useState<CreateDetails | null>();
  const [formValuesIVTAVT, setFormValuesIVTAVT] = React.useState<CreateDetails | null>(null);
  const [listContainerDefinitionsError, setListContainerDefinitionsError] =
    React.useState<string>('');
  const [getLocationsForSiteError, setGetLocationsForSiteError] = React.useState<string>('');
  const [typeSelectedValue, setTypeSelectedValue] = React.useState<string>('');
  const [typeSelectedOption, setTypeSelectedOption] = React.useState(null);
  const [failedRequests, setFailedRequests] = React.useState<CreateDetails | null>(null);
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [siteLaunched, setPhase2SiteLaunched] = React.useState<boolean>();
  const [userAlias, setUserAlias] = React.useState('');
  const [categoriesMap, setCategoriesMap] = React.useState<Map<string, Category> | null>(null);
  const [containerDefinitionMap, setContainerDefinitionMap] = React.useState<Map<
    string,
    ContainerDefinition
  > | null>(null);

  const [location, setLocation] = React.useState(() => {
    // getting locationID to get sites
    const loc = localStorage.getItem('siteId');
    if (loc) {
      return JSON.parse(loc).locationId;
    }
    return '';
  });
  const [siteName, setSiteName] = React.useState(() => {
    // getting selected site name
    const loc = localStorage.getItem('siteId');
    if (loc) {
      return JSON.parse(loc).value;
    }
    return '';
  });
  const [csvData, setCsvData] = React.useState([]);
  const [csvFileName, setCsvFileName] = React.useState('');

  const breadcrumbs = [
    {
      text: t('breadcrumb.home'),
      href: '/',
    },
    {
      text: t('breadcrumb.inventory-management'),
      href: '/assets-inventory/',
    },
    {
      text: t('breadcrumb.container-management'),
      href: '/assets-inventory/container',
    },
    {
      text: t('breadcrumb.create-container'),
      href: '/assets-inventory/container/create',
    },
  ];

  // Query listContainerTypes
  const [listContainerTypes, { loading: listContainerTypesLoading }] = useLazyQuery(
    LIST_CONTAINER_TYPES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        if (result?.listContainerTypes?.containerTypes == null) {
          setListContainerTypesError('ContainerTypes result is null');
        } else if (siteLaunched === true) {
          setContainerTypeOptions(
            result.listContainerTypes.containerTypes.map((type: string) => {
              return {
                label: t(`container-management.${type}`),
                value: type,
              };
            })
          );
          setListContainerTypesError('');
        } else {
          // TODO: Replace `else` with the code in `else if` after CMS Expansion launch is complete.
          // Hard-coding values here because this code is temporary.
          const nonMovableTypes: any = ['bin', 'ivt', 'avt'] as const;
          setContainerTypeOptions(
            nonMovableTypes.map((type: string) => {
              return {
                label: t(`container-management.${type}`),
                value: type,
              };
            })
          );
          setListContainerTypesError('');
        }
      },
      onError: (error) => {
        setListContainerTypesError(error.message);
      },
    }
  );

  // Query listContainerDefinitions
  const [listContainerDefinitions, { loading: listContainerDefintionsLoading }] = useLazyQuery(
    GET_LIST_CONTAINER_DEFINITION,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        const [setCategoriesMapResult, containerDefinitionMapResult] =
          parseListContainerDefinitionsResponse(
            result.listContainerDefinitions.containerType,
            result.listContainerDefinitions.categories
          );
        setCategoriesMap(setCategoriesMapResult);
        setContainerDefinitionMap(containerDefinitionMapResult);
        setListContainerDefinitionsError('');
      },
      onError: (error) => {
        setListContainerDefinitionsError(error.message);
      },
    }
  );

  // Query getLocationsForSite
  const [getLocationsForSiteAPI, { loading: getLocationsForSiteLoading }] = useLazyQuery(
    GET_LOCATIONS_FOR_SITE,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        setRooms(transformDataForDropdown(result.getLocationsForSite.locations));
        setGetLocationsForSiteError('');
      },
      onError: (error) => {
        setGetLocationsForSiteError(error.message);
        setRooms([]);
      },
    }
  );

  // Query CMS feature flag API
  const [getCmsAppConfig, { loading: getCmsAppConfigLoading }] = useLazyQuery(CMS_APP_CONFIG, {
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      if (result?.getAppConfig?.value === 'true') {
        setPhase2SiteLaunched(true);
      } else {
        setPhase2SiteLaunched(false);
      }
    },
    onError: (error) => {
      setPhase2SiteLaunched(false);
    },
  });

  // query manageContainerPermissions
  const [
    callManageContainerPermissions,
    { loading: manageContainerPermissionsLoading, error: manageContainerPermissionsError },
  ] = useLazyQuery(MANAGE_CONTAINER_PERMISSIONS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setUserAlias(result?.manageContainerPermissionsCheck?.user);
    },
  });

  useEffect(() => {
    callManageContainerPermissions();
    getCmsAppConfig({ variables: { name: 'CMSPhase2Sites', key: siteName } });
    listContainerTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRooms([]);
    getLocationsForSiteAPI({ variables: { locationId: location } });
    getCmsAppConfig({ variables: { name: 'CMSPhase2Sites', key: siteName } });
    listContainerTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // resets data
  const resetFields = () => {
    setCategoriesMap(null);
    setContainerDefinitionMap(null);
    setFormValuesBin(null);
    setFormValuesIVTAVT(null);
    setHasSubmitted(false);
    setFailedRequests(null);
    setCreateContainersRequest(null);
  };

  // Handles type change
  const handleTypeChange = (obj: any) => {
    setTypeSelectedOption(obj);
    listContainerDefinitions({ variables: { containerType: obj.value } });
    setTypeSelectedValue(obj.value);
    resetFields();
  };

  // Creates dropdown with parameters
  const createDropDown = (option: any, onchange: any, options: any, name: any) => {
    return (
      <Select
        selectedOption={option}
        onChange={({ detail }) => onchange(detail.selectedOption)}
        options={options}
        placeholder="Choose an option"
        selectedAriaLabel="Selected"
        empty="No options"
        className={name}
        name={name}
      />
    );
  };

  const getCreateComponent = () => {
    if (listContainerDefintionsLoading) {
      return <Spinner size="big" />;
    }
    if (typeSelectedValue === '') {
      return <div />;
    }
    if (typeSelectedValue === 'ivt' || typeSelectedValue === 'avt') {
      return (
        <CreateIVTAVT
          setFormValuesIVTAVT={setFormValuesIVTAVT}
          setHasSubmitted={setHasSubmitted}
          data={{
            categoriesMap,
            containerDefinitionMap,
            rooms,
            sizeData,
            failedRequests,
          }}
          csvData={csvData}
          csvFileName={csvFileName}
        />
      );
    }
    if (typeSelectedValue === 'bin') {
      return (
        <CreateBin
          setFormValuesBin={setFormValuesBin}
          setHasSubmitted={setHasSubmitted}
          categoriesMap={categoriesMap}
          containerDefinitionMap={containerDefinitionMap}
          rooms={rooms}
          flex={formValuesBin?.flex || false}
          virtual={formValuesBin?.virtual || false}
          validations={formValuesBin?.validations || false}
          failedRequests={failedRequests}
          csvData={csvData}
          csvFileName={csvFileName}
        />
      );
    }
    // TODO accomodate ivt/avt/bin in CreateContainerComponent
    return (
      <CreateContainerComponent
        setCreateContainersRequest={setCreateContainersRequest}
        setHasSubmitted={setHasSubmitted}
        hasSubmitted={hasSubmitted}
        categoriesMap={categoriesMap}
        containerDefinitionMap={containerDefinitionMap}
        roomOptions={rooms}
        sizeOptions={sizeData}
        userAlias={userAlias}
        siteLocationId={location}
      />
    );
  };

  if (manageContainerPermissionsError) {
    return <Redirect to="/container" />;
  }

  return (
    <div key="landingForm" className="formWidth">
      <SpaceBetween direction="vertical" size="s">
        <BreadcrumbGroup items={breadcrumbs} />

        {(listContainerTypesLoading ||
          getLocationsForSiteLoading ||
          getCmsAppConfigLoading ||
          manageContainerPermissionsLoading) && <Spinner size="big" />}
        {listContainerTypesError && (
          <Alert type="error">Error listing container types: {listContainerTypesError}</Alert>
        )}
        {listContainerDefinitionsError && (
          <Alert type="error">
            Error listing container definitions: {listContainerDefinitionsError}
          </Alert>
        )}
        {getLocationsForSiteError && (
          <Alert type="error">GetLocationsForSite: {getLocationsForSiteError}</Alert>
        )}
        {formValuesIVTAVT && (
          // TODO deprecate Create for CreateContainerAlerts
          <Create
            setFailedRequests={setFailedRequests}
            setHasSubmitted={setHasSubmitted}
            hasSubmitted={hasSubmitted}
            data={{
              formValues: formValuesIVTAVT.formValues,
              binDetails: formValuesIVTAVT.binDetails,
              binDetailsFlex: formValuesIVTAVT.binDetailsFlex,
            }}
            csvData={csvData}
            csvFileName={csvFileName}
            setCsvData={setCsvData}
            setCsvFileName={setCsvFileName}
          />
        )}
        {formValuesBin && (
          // TODO deprecate Create for CreateContainerAlerts
          <Create
            setFailedRequests={setFailedRequests}
            setHasSubmitted={setHasSubmitted}
            hasSubmitted={hasSubmitted}
            data={{
              formValues: formValuesBin.formValues,
              binDetails: formValuesBin.binDetails,
              binDetailsFlex: formValuesBin.binDetailsFlex,
            }}
            csvData={csvData}
            csvFileName={csvFileName}
            setCsvData={setCsvData}
            setCsvFileName={setCsvFileName}
          />
        )}
        {createContainersRequest && (
          <CreateContainerAlerts
            createContainersRequest={createContainersRequest}
            setCreateContainersResponse={setCreateContainersResponse}
            createContainersResponse={createContainersResponse}
            setHasSubmitted={setHasSubmitted}
            hasSubmitted={hasSubmitted}
          />
        )}

        <SpaceBetween direction="vertical" size="xs">
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <SiteInput
                onSiteChange={(loc) => {
                  setLocation(loc?.locationId);
                  setSiteName(loc?.value);
                }}
              />
            </SpaceBetween>
          </Box>
          <Container header={<Header variant="h2">Create</Header>}>
            <SpaceBetween direction="vertical" size="l">
              <FormField label="Type">
                <div className="dropDownStyle">
                  {createDropDown(
                    typeSelectedOption,
                    handleTypeChange,
                    containerTypeOptions,
                    'type'
                  )}
                </div>
              </FormField>

              {getCreateComponent()}
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </SpaceBetween>
    </div>
  );
};

export default CreateContainer;
